import { AppModule } from 'app/module';
import { user } from 'app/user';
import { Input } from 'module/input/main';
import { Toggle } from 'module/toggle/main';
import { postJson, deleteJson } from 'util/xhr';
import { serializeJson } from 'util/form';
import { trackGa4 } from 'util/googleAnalytics';
import { GdprModal } from 'module/gdprModal/main';
import { Spot } from 'module/spot/main';
import { API_ENDPOINT_JOBALARM_CREATE, API_ENDPOINT_JOBALARM_DELETE } from 'app/endpoints';
import { MAIL_DAILY, MAIL_WEEKLY } from 'app/mailSubscriptionInterval';
import * as jobAlarmResultState from 'util/jobAlarmResultStates';
import { ResultState } from 'module/resultState/main';
import { JobAlarmNoSpamText } from 'module/jobAlarmNoSpamText/main';
import { generic } from 'util/validationMessages';
import Template from './template.hbs';
import './styles.scss';

export class AlarmDisruptor extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    domBindings() {
        return {
            form: '.m-alarmDisruptor__form',
            email: '.m-alarmDisruptor__inputGroup .m-input',
            emailValidationMessage: '.m-alarmDisruptor__validationMessageOutlet',
            inner: '.m-alarmDisruptor__inner',
            closeIcon: '.m-alarmDisruptor__closeSvg',
            anchor: '.m-alarmDisruptor__anchor',
            submitButton: '.m-alarmDisruptor__submitButton',
            gdprModal: '.m-alarmDisruptor__gdprModal .m-gdprModal',
            container: '.m-alarmDisruptor__container',
            toggle: '.m-alarmDisruptor__toggle .k-c-toggle',
            buttonLoggedOut: '.m-alarmDisruptor__button--loggedOut',
            button: '.m-alarmDisruptor__button',
            box: '.m-alarmDisruptor__box',
            inputKeyword: '.m-alarmDisruptor__input--keywords',
            inputLocations: '.m-alarmDisruptor__input--locations',
            inputRadius: '.m-alarmDisruptor__input--radius',
            inputHomeoffice: '.m-alarmDisruptor__input--homeoffice',
            inputJobFields: '.m-alarmDisruptor__input--jobFields',
            inputEmploymentTypes: '.m-alarmDisruptor__input--employmentTypes',
            inputJobLevels: '.m-alarmDisruptor__input--jobLevels',
            spot: '.m-alarmDisruptor__boxIcon',
            resultState: '.m-alarmDisruptor__resultState',
            noSpamText: '.m-jobAlarmNoSpamText',
        };
    }

    getPropsFromDom() {
        return {
            active: !!this.dom.inner,
            isLoggedIn: (this.dom.el.getAttribute('data-is-loggedin') === 'true'),
            similarJobsAlarm: this.dom.inner ? this.dom.inner.getAttribute('data-is-similar') : false,
            isInline: this.dom.inner ? (this.dom.inner.getAttribute('data-is-inline') === 'true') : false,
            displayNone: this.dom.box
                ? this.dom.box.classList.contains('m-alarmDisruptor__box--displayNone')
                : false,
            email: this.dom.form ? this.dom.form.email.value : false,
            isJobDetail: (this.dom.el.getAttribute('data-is-job-detail') === 'true'),
            query: {
                keywords: this.dom.inputKeyword ? this.dom.inputKeyword.value : '',
                locations: this.dom.inputLocations ? this.dom.inputLocations.value : '',
                radius: this.dom.inputRadius ? this.dom.inputRadius.value : '',
                homeoffice: this.dom.inputHomeoffice ? this.dom.inputHomeoffice.value : false,
                jobFields: this.dom.inputJobFields ? this.dom.inputJobFields.value : '',
                employmentTypes: this.dom.inputEmploymentTypes ? this.dom.inputEmploymentTypes.value : '',
                jobLevels: this.dom.inputJobLevels ? this.dom.inputJobLevels.value : '',
            },
            keywords: this.dom.box ? this.dom.box.getAttribute('data-keywords') : false,
            location: this.dom.box ? this.dom.box.getAttribute('data-location') : false,
            jobAlarmId: this.dom.button ? this.dom.button.getAttribute('data-jobalarm-id') : false,
            exists: this.dom.button ? this.dom.button.getAttribute('data-jobalarm-id') : false,
        };
    }

    domEvents() {
        if (this.dom.buttonLoggedOut) {
            this.dom.buttonLoggedOut.addEventListener('click', () => this.openAlarmBox());
        }

        if (this.dom.closeIcon) {
            this.dom.closeIcon.addEventListener('click', () => this.closeAlarmBox());
        }

        if (this.dom.form) {
            this.dom.form.addEventListener('submit', (e) => this.submit(e));
        }
    }

    closeAlarmBox() {
        this.updateProps({ displayNone: true, success: false }, true);
    }

    openAlarmBox(timeout = false) {
        if (localStorage?.getItem?.('wasOpen') !== 'true') {
            this.updateProps({ displayNone: false }, true);
        } else if (!timeout) {
            this.updateProps({ displayNone: false }, true);
        }

        localStorage?.setItem?.('wasOpen', true);
    }

    validationRules() {
        this.validation.useMessages(generic);
        this.validation.addRule(this.email, {
            rules: ['required', 'email'],
        });
    }

    subs() {
        if (this.dom.gdprModal) {
            this.gdprModal = new GdprModal(this.dom.gdprModal);
        }

        if (this.dom.toggle) {
            this.toggle = new Toggle(this.dom.toggle);
        }

        if (this.dom.email) {
            this.email = new Input(this.dom.email);
        }

        if (this.dom.spot) {
            this.spot = new Spot(this.dom.spot);
        }

        if (this.dom.resultState) {
            this.resultState = new ResultState(this.dom.resultState);
        }

        if (this.dom.noSpamText) {
            this.noSpamText = new JobAlarmNoSpamText(this.dom.noSpamText);
        }
    }

    subEvents() {
        if (this.gdprModal) {
            this.gdprModal.events.on('acceptGdpr', () => this.acceptGdpr());
            this.gdprModal.events.on('removeModalStyle', () => this.closeGdprModal());
        }

        if (this.dom.toggle) {
            this.toggle.events.on('toggleClick', (e) => this.toggleAlarm(e));
        }
        if (this.email) {
            this.email.events.on('blur', (e) => {
                this.updateProps({ emailInputValue: e.arguments[0].target.value });
            });
        }
    }

    toggleAlarm(e) {
        const isActive = e.arguments[0];

        if (isActive) {
            if (this.validation.validate()) {
                this.createAlarm(serializeJson(this.dom.form));
            }
        } else {
            this.removeAlarm(serializeJson(this.dom.form));
        }
    }

    submit(e) {
        e.preventDefault();
        if (this.validation.validate()) {
            this.createAlarm(serializeJson(this.dom.form));
        }
    }

    createAlarm(data) {
        if (this.request) {
            this.request.abort();
        }

        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = true;
        }

        this.request = postJson(
            API_ENDPOINT_JOBALARM_CREATE,
            { data },
            (response) => this.onResponse(response),
            (error) => this.onResponseError(error),
        );
    }

    removeAlarm(data) {
        if (this.request) {
            this.request.abort();
        }

        const url = `${API_ENDPOINT_JOBALARM_DELETE}/${this.props.jobAlarmId}`;
        this.request = deleteJson(
            url,
            { data },
            (response) => this.onRemoveResponse(response),
        );
    }

    onRemoveResponse(response, calledFromController = false) {
        this.updateProps(Object.assign(response, { success: false }), true);
        if (!calledFromController && user.isLoggedIn()) {
            this.events.emit('jobAlarmRemoveResponse', response);
            this.events.emit('showSnackbar');
        }
    }

    onResponse(response, calledFromController = false) {
        if (response.gdpr) {
            this.gdprModal.setProps(response.gdpr);
            this.gdprModal.updateProps({ visible: true, scrollTop: 0, active: true }, true);
            this.gdprModal.checkIfScrollEnd();
            this.events.emit('addModalStyle');
        } else {
            let resultState;
            if (response.userExists) {
                resultState = jobAlarmResultState
                    .success(response.email, response.jobAlarmId, response.jobAlarmMailDaily);
            } else {
                resultState = jobAlarmResultState.checkMail();
            }

            this.updateProps({
                ...response,
                resultState,
            }, true);
            if (!calledFromController) {
                const event = {
                    event: 'jobalarm_create_success',
                    user_data: {
                        email: response.email,
                    },
                };

                if (this.props.isJobDetail) {
                    trackGa4({
                        ...event,
                        method: 'Job Alarm Detail',
                    });
                } else if (this.props.isInline) {
                    trackGa4({
                        ...event,
                        method: 'Job Alarm Inline',
                    });
                } else {
                    trackGa4({
                        ...event,
                        method: 'Job Alarm Fixed',
                    });
                }
                if (user.isLoggedIn()) {
                    this.events.emit('jobAlarmResponseSuccess', response);
                    let snackbarText;

                    if (parseInt(response.mailNotificationInterval, 10) === MAIL_DAILY) {
                        snackbarText = 'Ab sofort erhältst du täglich neue Jobs per E-Mail.';
                    } else if (parseInt(response.mailNotificationInterval, 10) === MAIL_WEEKLY) {
                        snackbarText = 'Ab sofort erhältst du wöchentlich neue Jobs per E-Mail';
                    }

                    this.events.emit(
                        'showSnackbar',
                        snackbarText,
                        '/settings/notifications#c-notificationIndex-jobalarm',
                        'Anpassen',
                    );
                }
            }
        }
    }

    onResponseError(response) {
        const errorsMeta = this.validation.handleBackendValidationErrors(response);

        // From here on it's custom component error handling

        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = false;
        }

        const error = errorsMeta?.general;
        let resultState;
        if (error?.[0].jobalarm_exists) {
            resultState = jobAlarmResultState.alreadyExists();
        } else if (error?.[0].jobalarm_limit_exceeded) {
            resultState = jobAlarmResultState.limitExceeded();
        } else if (error?.[0].jobalarm_exists_for_unconfirmed_email) {
            resultState = jobAlarmResultState.checkMail();
        } else {
            resultState = jobAlarmResultState.generalError();
        }
        this.updateProps({
            resultState,
            displayNone: false,
        }, true);

        if (this.email) {
            this.email.updateProps({ value: this.props.emailInputValue }, true);
        }

        return true;
    }

    acceptGdpr() {
        if (this.gdprModal.validate()) {
            const gdprOptionalCheckBoxSize = Object.keys(this.gdprModal.props.optionalBox.items).length;
            const gdprRequiredCheckBoxSize = Object.keys(this.gdprModal.props.requiredBox.items).length;
            const data = serializeJson(this.dom.form);

            for (let i = 0; i < gdprOptionalCheckBoxSize; i += 1) {
                data[this.gdprModal.props.optionalBox.items[i].name] = this.gdprModal.props.optionalBox.items[i].checked;
            }

            for (let i = 0; i < gdprRequiredCheckBoxSize; i += 1) {
                data[this.gdprModal.props.requiredBox.items[i].name] = this.gdprModal.props.requiredBox.items[i].checked;
            }

            this.gdprModal.closeModal();
            this.createAlarm(data);
        }
    }

    closeGdprModal() {
        this.events.emit('removeModalStyle');
        if (this.dom.submitButton) {
            this.dom.submitButton.disabled = false;
        }
    }
}
